import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  useListInventoryPlans,
  useListPlanEntitlements,
  useListInventoryItems,
  useCreatePlanEntitlement,
  useUpdatePlanEntitlement,
  useDeletePlanEntitlement,
  getListPlanEntitlementsQueryKey,
} from "@workspace/api-client-react";
import type { PlanEntitlement } from "@workspace/api-client-react";

const CreatePlanEntitlementBody = z.object({
  planId: z.number().min(1, "Plan is required"),
  itemId: z.number().min(1, "Item is required"),
  quotaQuantity: z.number().min(0),
  // Phase 10 extended QuotaPeriod enum with per_visit, per_booking, per_hour
  quotaPeriod: z.enum([
    "per_day",
    "per_month",
    "per_lifetime",
    "per_membership",
    "per_visit",
    "per_booking",
    "per_hour",
  ]),
  overageRate: z.number().nullable().optional(),
  carryForward: z.boolean().nullable().optional(),
});
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { plnId, itmId } from "@/lib/display-id";

type FormValues = z.infer<typeof CreatePlanEntitlementBody>;

interface Props {
  openCreate: boolean;
  setOpenCreate: (open: boolean) => void;
}

function PlanEntitlementsCard({ planId, planName, openCreate, setOpenCreate }: {
  planId: number;
  planName: string;
  openCreate: boolean;
  setOpenCreate: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editItem, setEditItem] = useState<PlanEntitlement | null>(null);
  const [deleteItem, setDeleteItem] = useState<PlanEntitlement | null>(null);

  const { data: entitlements, isLoading } = useListPlanEntitlements({ planId });
  const { data: allItems } = useListInventoryItems();
  const { data: allPlans } = useListInventoryPlans();

  const eligibleItems = allItems?.filter(
    (item) =>
      item.chargingModel === "Complimentary_With_Limit" ||
      item.chargingModel === "Plan_Inclusive_Quota"
  );

  const createMutation = useCreatePlanEntitlement();
  const updateMutation = useUpdatePlanEntitlement();
  const deleteMutation = useDeletePlanEntitlement();

  const form = useForm<FormValues>({
    resolver: zodResolver(CreatePlanEntitlementBody),
    defaultValues: {
      planId,
      itemId: 0,
      quotaQuantity: 0,
      quotaPeriod: "per_month",
      carryForward: false,
    },
  });

  const isOpen = openCreate || editItem !== null;
  const isEditMode = editItem !== null;

  function openEdit(item: PlanEntitlement) {
    setEditItem(item);
    form.reset({
      planId: item.planId,
      itemId: item.itemId,
      quotaQuantity: item.quotaQuantity,
      quotaPeriod: item.quotaPeriod,
      overageRate: item.overageRate ?? undefined,
      carryForward: item.carryForward,
    });
  }

  function handleClose() {
    setOpenCreate(false);
    setEditItem(null);
    form.reset({ planId, itemId: 0, quotaQuantity: 0, quotaPeriod: "per_month", carryForward: false });
  }

  async function onSubmit(values: FormValues) {
    try {
      if (isEditMode) {
        await updateMutation.mutateAsync({ id: editItem.id, data: values });
      } else {
        await createMutation.mutateAsync({ data: values });
      }
      toast({ title: "Entitlement saved." });
      queryClient.invalidateQueries({ queryKey: getListPlanEntitlementsQueryKey({ planId }) });
      handleClose();
    } catch {
      toast({ title: "Something went wrong. Please try again.", variant: "destructive" });
    }
  }

  async function handleDelete() {
    if (!deleteItem) return;
    try {
      await deleteMutation.mutateAsync({ id: deleteItem.id });
      toast({ title: "Entitlement removed." });
      queryClient.invalidateQueries({ queryKey: getListPlanEntitlementsQueryKey({ planId }) });
      setDeleteItem(null);
    } catch {
      toast({ title: "Something went wrong. Please try again.", variant: "destructive" });
    }
  }

  const getItemName = (itemId: number) =>
    allItems?.find((i) => i.id === itemId)?.name ?? `Item #${itemId}`;

  return (
    <>
      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-base">
            {plnId(planId)} — {planName}{" "}
            <span className="text-muted-foreground font-normal text-sm">
              ({entitlements?.length ?? 0} entitlements)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead>Quota Amount</TableHead>
                <TableHead>Quota Period</TableHead>
                <TableHead>Overage Rate ₹</TableHead>
                <TableHead>Carry Forward</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(3)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(6)].map((__, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-20" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : entitlements?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-16 text-center text-muted-foreground text-sm">
                    No entitlements for this plan.
                  </TableCell>
                </TableRow>
              ) : (
                entitlements?.map((ent) => (
                  <TableRow key={ent.id}>
                    <TableCell className="font-medium">
                      <span className="font-mono text-xs text-muted-foreground mr-1">{itmId(ent.itemId)}</span>
                      {getItemName(ent.itemId)}
                    </TableCell>
                    <TableCell>{ent.quotaQuantity}</TableCell>
                    <TableCell className="text-sm">{ent.quotaPeriod.replace(/_/g, " ")}</TableCell>
                    <TableCell>{ent.overageRate != null ? `₹${ent.overageRate}` : "—"}</TableCell>
                    <TableCell>{ent.carryForward ? "✓" : "—"}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(ent)}>Edit</Button>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setDeleteItem(ent)}>Remove</Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isOpen} onOpenChange={(o) => !o && handleClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isEditMode ? "Edit Entitlement" : "Add Entitlement"}</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
              <FormField
                name="planId"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Plan <span className="text-destructive">*</span></FormLabel>
                    <Select value={String(field.value)} onValueChange={(v) => field.onChange(Number(v))}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a plan" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {allPlans?.map((p) => (
                          <SelectItem key={p.id} value={p.id.toString()}>
                            {plnId(p.id)} — {p.planName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="itemId"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Item <span className="text-destructive">*</span></FormLabel>
                    <Select value={field.value ? String(field.value) : ""} onValueChange={(v) => field.onChange(Number(v))}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select an item" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {eligibleItems?.map((item) => (
                          <SelectItem key={item.id} value={item.id.toString()}>
                            {itmId(item.id)} — {item.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="quotaQuantity"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Quota Amount <span className="text-destructive">*</span></FormLabel>
                    <FormControl>
                      <Input type="number" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="quotaPeriod"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Quota Period <span className="text-destructive">*</span></FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="per_day">Per Day</SelectItem>
                        <SelectItem value="per_month">Per Month</SelectItem>
                        <SelectItem value="per_lifetime">Per Lifetime</SelectItem>
                        <SelectItem value="per_membership">Per Membership</SelectItem>
                        <SelectItem value="per_visit">Per Visit</SelectItem>
                        <SelectItem value="per_booking">Per Booking</SelectItem>
                        <SelectItem value="per_hour">Per Hour</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="overageRate"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Overage Rate ₹</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="Optional"
                        {...field}
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="carryForward"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2">
                    <FormLabel className="mt-0">Carry Forward</FormLabel>
                    <FormControl>
                      <Switch checked={field.value ?? false} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleClose}>
                  Discard changes
                </Button>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  {createMutation.isPending || updateMutation.isPending ? "Saving…" : "Save Entitlement"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteItem !== null} onOpenChange={(o) => !o && setDeleteItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Entitlement?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the entitlement from {planName}. This action can be undone by re-adding the entitlement.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function EntitlementsTab({ openCreate, setOpenCreate }: Props) {
  const { data: plans, isLoading } = useListInventoryPlans();

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <Skeleton className="h-40 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!plans?.length) {
    return (
      <Card>
        <CardContent className="h-24 flex items-center justify-center text-muted-foreground">
          No entitlements defined. Add entitlements to specify what each plan includes.
        </CardContent>
      </Card>
    );
  }

  return (
    <div>
      {plans.map((plan) => (
        <PlanEntitlementsCard
          key={plan.id}
          planId={plan.id}
          planName={plan.planName}
          openCreate={openCreate}
          setOpenCreate={setOpenCreate}
        />
      ))}
    </div>
  );
}
